import React, { memo, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { CheckSquare, ArrowRight, Bot, User, Clock, Link2, ExternalLink, Brain } from 'lucide-react';
import { format } from 'date-fns';

// Memoized Task Card component
const TaskCard = memo(({ task, onClick, followUpCount, dependencyCount, onViewOutput }) => (
  <div 
    className={`p-4 border rounded-lg hover:shadow-sm hover:border-blue-200 transition-all cursor-pointer ${task.is_follow_up ? 'border-l-4 border-l-amber-400 bg-amber-50/30' : ''}`}
    onClick={() => onClick(task)}
    data-testid={`task-card-${task.id}`}
  >
    <div className="flex items-start gap-3">
      <CheckSquare className={`h-5 w-5 mt-0.5 ${task.status === 'completed' ? 'text-emerald-600' : 'text-slate-400'}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className={`font-medium ${task.status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>
            {task.title}
          </p>
          {task.is_follow_up && (
            <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300 text-xs">
              <ArrowRight className="h-3 w-3 mr-1" /> Follow-up
            </Badge>
          )}
          {task.assigned_to_type === 'ai_agent' && task.cortex_synthesis && (
            <Badge variant="outline" className="bg-purple-100 text-purple-700 border-purple-300 text-xs">
              <Brain className="h-3 w-3 mr-1" /> Has Output
            </Badge>
          )}
        </div>
        
        {/* Assignment Info */}
        <div className="flex items-center gap-3 mt-2 flex-wrap">
          {(task.assigned_to || task.assigned_to_name) && (
            <div className="flex items-center gap-1.5 text-xs">
              {task.assigned_to_type === 'ai_agent' ? (
                <>
                  <div className="h-5 w-5 rounded-full bg-purple-100 flex items-center justify-center">
                    <Bot className="h-3 w-3 text-purple-600" />
                  </div>
                  <span className="text-purple-700 font-medium">{task.assigned_to_name || 'AI Agent'}</span>
                </>
              ) : (
                <>
                  <div className="h-5 w-5 rounded-full bg-blue-100 flex items-center justify-center">
                    <User className="h-3 w-3 text-blue-600" />
                  </div>
                  <span className="text-blue-700">{task.assigned_to_name || task.assigned_to || 'Unassigned'}</span>
                </>
              )}
            </div>
          )}
          
          {task.due_date && (
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Clock className="h-3 w-3" />
              Due: {format(new Date(task.due_date), 'MMM d')}
            </span>
          )}
          
          {dependencyCount > 0 && (
            <Badge variant="outline" className="text-xs bg-slate-50">
              <Link2 className="h-3 w-3 mr-1" />
              {dependencyCount} dependency
            </Badge>
          )}
          
          {/* View Output Button for AI tasks with results */}
          {task.assigned_to_type === 'ai_agent' && (task.cortex_synthesis || task.reasoning || task.deliverables_count > 0) && (
            <Button
              variant="outline"
              size="sm"
              className="h-6 text-xs bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100"
              onClick={(e) => { e.stopPropagation(); onViewOutput && onViewOutput(task); }}
            >
              <ExternalLink className="h-3 w-3 mr-1" /> View Output
            </Button>
          )}
        </div>
      </div>
      
      <div className="flex items-center gap-2">
        <Badge variant="outline" className={
          task.status === 'completed' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
          task.status === 'in_progress' ? 'bg-blue-50 text-blue-700 border-blue-200' :
          task.status === 'cancelled' ? 'bg-red-50 text-red-700 border-red-200' :
          'bg-slate-50 text-slate-700 border-slate-200'
        }>{task.status?.replace('_', ' ')}</Badge>
        {followUpCount > 0 && (
          <Badge className="bg-amber-100 text-amber-700 border-amber-300 text-xs">
            {followUpCount} follow-up{followUpCount > 1 ? 's' : ''}
          </Badge>
        )}
      </div>
    </div>
  </div>
));

TaskCard.displayName = 'TaskCard';

// Memoized Follow-up Task Card
const FollowUpTaskCard = memo(({ task, onClick }) => (
  <div 
    className="p-3 border rounded-lg hover:shadow-sm hover:border-amber-200 transition-all cursor-pointer bg-amber-50/50"
    onClick={() => onClick(task)}
    data-testid={`followup-task-card-${task.id}`}
  >
    <div className="flex items-start gap-2">
      <ArrowRight className="h-4 w-4 text-amber-500 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium ${task.status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>
          {task.title}
        </p>
        <div className="flex items-center gap-2 mt-1">
          {(task.assigned_to || task.assigned_to_name) && (
            <span className="text-xs flex items-center gap-1">
              {task.assigned_to_type === 'ai_agent' ? (
                <Bot className="h-3 w-3 text-purple-600" />
              ) : (
                <User className="h-3 w-3 text-blue-600" />
              )}
              {task.assigned_to_name || task.assigned_to}
            </span>
          )}
          {task.due_date && (
            <span className="text-xs text-muted-foreground">
              Due: {format(new Date(task.due_date), 'MMM d')}
            </span>
          )}
        </div>
      </div>
      <Badge variant="outline" className="text-xs">{task.status?.replace('_', ' ')}</Badge>
    </div>
  </div>
));

FollowUpTaskCard.displayName = 'FollowUpTaskCard';

// Memoized Orphan Follow-up Card
const OrphanFollowUpCard = memo(({ task, onClick }) => (
  <div 
    className="p-4 border rounded-lg border-l-4 border-l-amber-400 bg-amber-50/30 hover:shadow-sm hover:border-amber-200 transition-all cursor-pointer"
    onClick={() => onClick(task)}
    data-testid={`orphan-followup-task-${task.id}`}
  >
    <div className="flex items-start gap-3">
      <ArrowRight className="h-5 w-5 text-amber-500 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className={`font-medium ${task.status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>
            {task.title}
          </p>
          <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300 text-xs">
            Follow-up
          </Badge>
        </div>
        <div className="flex items-center gap-3 mt-2">
          {(task.assigned_to || task.assigned_to_name) && (
            <div className="flex items-center gap-1.5 text-xs">
              {task.assigned_to_type === 'ai_agent' ? (
                <>
                  <Bot className="h-3 w-3 text-purple-600" />
                  <span className="text-purple-700">{task.assigned_to_name || 'AI Agent'}</span>
                </>
              ) : (
                <>
                  <User className="h-3 w-3 text-blue-600" />
                  <span className="text-blue-700">{task.assigned_to_name || task.assigned_to}</span>
                </>
              )}
            </div>
          )}
          {task.due_date && (
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Clock className="h-3 w-3" />
              Due: {format(new Date(task.due_date), 'MMM d')}
            </span>
          )}
        </div>
      </div>
      <Badge variant="outline">{task.status?.replace('_', ' ')}</Badge>
    </div>
  </div>
));

OrphanFollowUpCard.displayName = 'OrphanFollowUpCard';

// Main TasksTab component
const TasksTab = memo(({ tasks, onTaskClick, onViewOutput }) => {
  const navigate = useNavigate();
  
  // Memoize task groupings
  const mainTasks = useMemo(() => 
    tasks.filter(t => !t.parent_task_id && !t.is_follow_up), 
    [tasks]
  );
  
  const orphanFollowUps = useMemo(() => 
    tasks.filter(t => t.is_follow_up && (!t.parent_task_id || !tasks.find(p => p.id === t.parent_task_id))),
    [tasks]
  );
  
  const getFollowUpTasks = useCallback((taskId) => 
    tasks.filter(t => t.parent_task_id === taskId || (t.is_follow_up && t.depends_on_task_ids?.includes(taskId))),
    [tasks]
  );
  
  const getDependencyCount = useCallback((taskDependsOn) => 
    taskDependsOn ? tasks.filter(t => taskDependsOn.includes(t.id)).length : 0,
    [tasks]
  );
  
  const handleViewOutput = useCallback((task) => {
    if (onViewOutput) {
      onViewOutput(task);
    } else if (task.assigned_to_type === 'ai_agent') {
      // Navigate to AI Workforce All Tasks tab with task ID
      navigate('/hr-hub/ai-workforce?tab=all-ai-tasks&taskId=' + task.id);
    }
  }, [navigate, onViewOutput]);

  if (tasks.length === 0) {
    return (
      <div className="text-center py-12">
        <CheckSquare className="h-12 w-12 text-slate-300 mx-auto mb-3" />
        <p className="text-muted-foreground">No tasks</p>
      </div>
    );
  }

  return (
    <ScrollArea className="h-[550px]">
      <div className="p-4">
        <div className="space-y-3">
          {mainTasks.map(task => {
            const followUpTasks = getFollowUpTasks(task.id);
            const dependencyCount = getDependencyCount(task.depends_on_task_ids);
            
            return (
              <div key={task.id} className="space-y-2">
                <TaskCard 
                  task={task} 
                  onClick={onTaskClick}
                  followUpCount={followUpTasks.length}
                  dependencyCount={dependencyCount}
                  onViewOutput={handleViewOutput}
                />
                
                {followUpTasks.length > 0 && (
                  <div className="ml-8 space-y-2 border-l-2 border-amber-200 pl-4">
                    {followUpTasks.map(followUp => (
                      <FollowUpTaskCard 
                        key={followUp.id} 
                        task={followUp} 
                        onClick={onTaskClick}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          
          {orphanFollowUps.map(task => (
            <OrphanFollowUpCard 
              key={task.id} 
              task={task} 
              onClick={onTaskClick}
            />
          ))}
        </div>
      </div>
    </ScrollArea>
  );
});

TasksTab.displayName = 'TasksTab';

export default TasksTab;
